/**
 * 活動開始 vs 場地租用時段（前端共用）
 * 約定：結束鐘面 ≤ 開始鐘面 = 隔日（進場日仍為 ACTIVITY_DATE）
 */

export const ORGANIZER_VENUE_MAX_MINUTES = 4 * 60;
const DAY_MIN = 24 * 60;

export function parseHmToMinutes(hm) {
  const s = String(hm || '').trim().slice(0, 5);
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return NaN;
  return h * 60 + min;
}

export function minsToHm(total) {
  const t = ((Number(total) % DAY_MIN) + DAY_MIN) % DAY_MIN;
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 結束鐘面相對開始：若結束 ≤ 開始則視為隔日（+1440） */
export function normalizeEndMinutesAfterStart(startMin, endMin) {
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return NaN;
  return endMin <= startMin ? endMin + DAY_MIN : endMin;
}

/** 活動時刻相對場地開始：若早於／等於開始鐘面且場地跨日，則視為隔日 */
export function normalizeActivityMinutesInVenueWindow(activityMin, venueStartMin, venueEndAbs) {
  if (!Number.isFinite(activityMin) || !Number.isFinite(venueStartMin)) return NaN;
  let at = activityMin;
  if (Number.isFinite(venueEndAbs) && venueEndAbs > DAY_MIN && at <= venueStartMin) {
    at += DAY_MIN;
  }
  return at;
}

export function isCrossMidnightHm(startHm, endHm) {
  const vs = parseHmToMinutes(startHm);
  const ve = parseHmToMinutes(endHm);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return false;
  return ve <= vs;
}

export function defaultVenueEndFromStart(venueStart, maxMinutes = ORGANIZER_VENUE_MAX_MINUTES) {
  const vs = parseHmToMinutes(venueStart);
  if (!Number.isFinite(vs)) return '';
  return minsToHm(vs + maxMinutes);
}

/** 活動開始可選：場地開始之後到場地結束（不含）的 15 分格（不可等於場地開始；支援跨日） */
export function activityStartOptionsInVenueWindow(venueStart, venueEnd) {
  const vs = parseHmToMinutes(venueStart);
  let ve = parseHmToMinutes(venueEnd);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return [];
  ve = normalizeEndMinutesAfterStart(vs, ve);
  if (!(ve > vs)) return [];
  const out = [];
  for (let t = vs + 15; t < ve; t += 15) {
    out.push(minsToHm(t));
  }
  return out;
}

export function clampActivityStartToVenue(activityTime, venueStart, venueEnd) {
  const opts = activityStartOptionsInVenueWindow(venueStart, venueEnd);
  if (!opts.length) return '';
  if (activityTime && opts.includes(activityTime)) return activityTime;
  return opts[0];
}

/**
 * 活動結束可選：活動開始之後的 15 分格。
 * 若有 venueEnd：上限為場地結束（不含）；支援跨日。
 * 若無 venueEnd：至當日 23:45。
 */
export function activityEndOptionsAfterStart(activityStart, venueEnd = null, venueStart = null) {
  const as = parseHmToMinutes(activityStart);
  if (!Number.isFinite(as)) return [];

  if (venueEnd) {
    const vs = Number.isFinite(parseHmToMinutes(venueStart))
      ? parseHmToMinutes(venueStart)
      : as;
    let ve = parseHmToMinutes(venueEnd);
    if (!Number.isFinite(ve)) return [];
    ve = normalizeEndMinutesAfterStart(vs, ve);
    const asAbs = normalizeActivityMinutesInVenueWindow(as, vs, ve);
    const upper = ve;
    const out = [];
    for (let t = asAbs + 15; t < upper; t += 15) {
      out.push(minsToHm(t));
    }
    return out;
  }

  const out = [];
  for (let t = as + 15; t < DAY_MIN; t += 15) {
    out.push(minsToHm(t));
  }
  return out;
}

/** 場地設定時預設活動結束＝場地結束前 15 分；否則活動開始 +1 小時 */
export function defaultActivityEnd(venueEnd, activityStart) {
  const as = parseHmToMinutes(activityStart);
  const ve = parseHmToMinutes(venueEnd);
  if (Number.isFinite(ve) && Number.isFinite(as)) {
    const veAbs = normalizeEndMinutesAfterStart(as, ve);
    const candidate = veAbs - 15;
    if (candidate > as) return minsToHm(candidate);
  } else if (Number.isFinite(ve) && !Number.isFinite(as)) {
    return minsToHm(ve - 15);
  }
  if (Number.isFinite(as)) return minsToHm(Math.min(as + 60, DAY_MIN - 15));
  return venueEnd ? minsToHm(parseHmToMinutes(venueEnd) - 15) : '';
}

export function formatActivityTimeRange(activityStart, activityEnd) {
  const a = String(activityStart || '').trim().slice(0, 5);
  const b = String(activityEnd || '').trim().slice(0, 5);
  if (a && b) {
    if (isCrossMidnightHm(a, b)) return `${a} ~ ${b}（隔日）`;
    return `${a} ~ ${b}`;
  }
  return a || b || '';
}

export function venueDurationOk(venueStart, venueEnd, maxMinutes = ORGANIZER_VENUE_MAX_MINUTES) {
  const vs = parseHmToMinutes(venueStart);
  let ve = parseHmToMinutes(venueEnd);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return false;
  ve = normalizeEndMinutesAfterStart(vs, ve);
  if (!(ve > vs)) return false;
  return ve - vs <= maxMinutes;
}
